/**
 * The one thing that differs between the portals' account screens: which
 * portal they are drawn for. Everything else — the flows, the forms, the
 * words, the Security settings — is this package's single copy.
 *
 * Each web portal mounts `AccountPortalProvider` once, near the root, with
 * its own values; the screens read them with `useAccountPortal`. A screen
 * rendered without the provider throws: a missing portal is a wiring defect,
 * and a default would hide it by quietly drawing the wrong portal.
 */
import { createContext, useContext, type ReactNode } from 'react';

/** Which portal a screen is drawn for — its brand panel, its copy, its telemetry tag. */
export type AccountPortal = 'citizen' | 'business';

export interface AccountPortalConfig {
  portal: AccountPortal;
  /** The language picker in the auth frame's top corner — the only one before sign-in. */
  languageSelector: ReactNode;
  /**
   * Where a finished sign-in goes: an in-app path, or an absolute URL (a
   * satellite's handshake). Read at the moment of completion, so it can
   * depend on the URL or on an invitation stashed along the way.
   */
  afterSignIn: () => string;
  /** Where a finished sign-up goes — same rules as `afterSignIn`. */
  afterSignUp: () => string;
  /** The terms and privacy pages: absolute URLs, or in-app paths. */
  legal: { terms: string; privacy: string };
  /** Where a provider returns the person after connecting an account in Settings. */
  securitySettingsPath: string;
  /** Whether sign-in may ask for an authenticator code (the portal's ENABLE_2FA). */
  allowTotp: boolean;
}

const AccountPortalContext = createContext<AccountPortalConfig | null>(null);

export function AccountPortalProvider({
  value,
  children,
}: {
  value: AccountPortalConfig;
  children: ReactNode;
}) {
  return <AccountPortalContext.Provider value={value}>{children}</AccountPortalContext.Provider>;
}

export function useAccountPortal(): AccountPortalConfig {
  const config = useContext(AccountPortalContext);
  if (!config) {
    throw new Error('useAccountPortal: render inside <AccountPortalProvider> (@tonnie/account).');
  }
  return config;
}

/**
 * Go to where a finished flow points: an in-app path through the router, an
 * absolute URL (another origin finishing Clerk's satellite handshake) by a
 * real navigation, so the browser actually leaves.
 */
export function goToDestination(target: string, navigate: (path: string) => void): void {
  if (target.startsWith('/') && !target.startsWith('//')) navigate(target);
  else window.location.assign(target);
}
